import React, { useRef, useState } from "react";
import { X, Maximize2 } from "lucide-react";
import PinDetailsPopover from "./PinDetailsPopover";

/**
 * SitePinDesigner — when a site image is uploaded, this lets the owner / staff
 * mark exactly WHICH surface in the real room photo gets WHICH material sample.
 *
 * UX (mobile-first, tap-driven so it works on iPad in the showroom):
 *   1. Tap a material letter (A / B / C / D) → that letter becomes the
 *      "active brush".
 *   2. Tap anywhere on the site image → a pin with that letter is dropped at
 *      that spot. Tap again on another surface → another pin for the same
 *      material (so A can sit on the wardrobe AND on the bed back).
 *   3. Tap the × on any pin to remove it.
 *   4. The list of pins is hoisted to the parent (App.js) and sent to the
 *      backend on Generate — the backend's prompt explains to Gemini what
 *      material goes where.
 *
 * Pin coordinates are stored as 0–1 fractions of the image's natural
 * dimensions, so they remain accurate regardless of the displayed size.
 */
const LETTER_COLORS = {
  A: { bg: "#b4914a", ring: "#8d6f33" }, // gold
  B: { bg: "#6b7a3d", ring: "#525e2d" }, // olive
  C: { bg: "#a85a3a", ring: "#7e3e26" }, // terracotta
  D: { bg: "#3d6b75", ring: "#284954" }, // teal
};

export { LETTER_COLORS };

export default function SitePinDesigner({
  siteImage,
  products,
  pins,
  setPins,
}) {
  const stageRef = useRef(null);
  const [activeLetter, setActiveLetter] = useState(null);
  const [openPinId, setOpenPinId] = useState(null);  // Phase 2 — which pin's popover is open
  const [imageAspect, setImageAspect] = useState(16 / 9);  // natural w/h — used to size the stage so portrait photos aren't cropped
  const [zoomOpen, setZoomOpen] = useState(false);  // Fullscreen preview of the site photo

  if (!siteImage || products.length === 0) return null;

  // The pin alphabet (A, B, C, D) maps 1:1 to product order.
  const letterFor = (idx) => String.fromCharCode(65 + idx);

  // How many of each letter is currently placed (used in the legend chip).
  const countFor = (letter) => pins.filter((p) => p.letter === letter).length;

  const handleStageTap = (e) => {
    if (!activeLetter) return;
    const rect = stageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    setPins([...pins, { letter: activeLetter, x, y, id: `${activeLetter}-${Date.now()}` }]);
  };

  const handleRemove = (id, ev) => {
    ev.stopPropagation();
    setPins(pins.filter((p) => p.id !== id));
  };

  return (
    <div
      data-testid="site-pin-designer"
      className={
        zoomOpen
          ? "fixed inset-0 z-[100] flex flex-col gap-2 bg-black/95 p-3 sm:p-4"
          : "mt-3"
      }
    >
      {/* When fullscreen — close button + dark-mode hint */}
      {zoomOpen && (
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">
            Fullscreen · tap letters & surface · tap pins for details
          </div>
          <button
            type="button"
            data-testid="site-zoom-close"
            onClick={() => {
              setOpenPinId(null);
              setZoomOpen(false);
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-heda-ink shadow-lg hover:bg-heda-cream"
            title="Close fullscreen"
          >
            <X size={16} strokeWidth={2.4} />
          </button>
        </div>
      )}

      {/* Letter palette — slim, borderless, classy. Tap to activate, then tap the photo to drop. */}
      <div
        className={[
          "flex flex-wrap items-center gap-1.5",
          zoomOpen ? "" : "mb-2",
        ].join(" ")}
      >
        {products.map((p, i) => {
          const letter = letterFor(i);
          const isActive = activeLetter === letter;
          const count = countFor(letter);
          const color = LETTER_COLORS[letter] || LETTER_COLORS.A;
          return (
            <button
              key={p.id || i}
              type="button"
              data-testid={`pin-letter-${letter}`}
              onClick={() => setActiveLetter(isActive ? null : letter)}
              className={[
                "group flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-[10.5px] font-medium transition-all",
                isActive
                  ? "text-white"
                  : zoomOpen
                    ? "text-white/70 hover:text-white"
                    : "text-heda-mute hover:text-heda-ink",
              ].join(" ")}
              style={
                isActive
                  ? { backgroundColor: color.bg, boxShadow: `0 1px 6px ${color.bg}55` }
                  : undefined
              }
              title={isActive ? "Active — tap the photo to place" : "Tap, then tap the site photo"}
            >
              <span
                className="flex h-4 w-4 items-center justify-center rounded-full text-[9.5px] font-bold text-white"
                style={{ backgroundColor: isActive ? "rgba(255,255,255,.22)" : color.bg }}
              >
                {letter}
              </span>
              {count > 0 && (
                <span
                  className={[
                    "tabular-nums text-[9.5px] font-semibold",
                    isActive ? "text-white/95" : "text-heda-gold",
                  ].join(" ")}
                >
                  ×{count}
                </span>
              )}
            </button>
          );
        })}
        {pins.length > 0 && (
          <button
            type="button"
            data-testid="pins-clear"
            onClick={() => setPins([])}
            className={[
              "ml-auto text-[10px] font-medium uppercase tracking-wider",
              zoomOpen ? "text-white/70 hover:text-white" : "text-heda-mute/80 hover:text-heda-ink",
            ].join(" ")}
          >
            Clear pins
          </button>
        )}
      </div>

      {/* Pin density warning — too many pins makes the photo unreadable to the AI */}
      {pins.length >= 9 && (
        <div
          data-testid="pin-density-warning"
          className={[
            "mb-2 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[10.5px]",
            pins.length > 20
              ? (zoomOpen ? "bg-red-500/15 text-red-200" : "bg-red-50 text-red-700")
              : (zoomOpen ? "bg-amber-500/15 text-amber-200" : "bg-amber-50/80 text-amber-800"),
          ].join(" ")}
        >
          <span>⚠</span>
          <span>
            {pins.length > 20
              ? `${pins.length} pins is too many (max 20). Group adjacent shutters with the same material under one pin.`
              : `${pins.length} pins — adjacent shutters with the same material can share ONE pin. 4-8 pins gives the best results.`}
          </span>
        </div>
      )}

      {/* Site image stage — tap to drop active pin.
          The stage matches the photo's NATURAL aspect ratio so portrait
          photos are not cropped to 16:9. In fullscreen mode, the stage
          expands to fill the available viewport height; otherwise capped
          at 75vh inline. */}
      <div
        className={
          zoomOpen
            ? "flex min-h-0 flex-1 items-center justify-center"
            : ""
        }
      >
      <div
        ref={stageRef}
        data-testid="site-stage"
        onClick={handleStageTap}
        style={{
          aspectRatio: imageAspect ? `${imageAspect}` : "16 / 9",
          maxHeight: zoomOpen ? "calc(100vh - 100px)" : "75vh",
          maxWidth: "100%",
        }}
        className={[
          "relative mx-auto overflow-hidden rounded-xl border bg-black/80 select-none",
          zoomOpen ? "h-full" : "w-full",
          activeLetter ? "border-heda-gold cursor-crosshair ring-2 ring-heda-gold/30" : "border-heda-line",
        ].join(" ")}
      >
        <img
          src={siteImage}
          alt="Site"
          draggable={false}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            if (w && h) setImageAspect(w / h);
          }}
          className="pointer-events-none h-full w-full object-contain"
        />

        {/* Fullscreen / preview button — top-right. Lets staff inspect the
            site photo in detail (especially useful on mobile / iPad).
            Hidden when already in fullscreen mode. */}
        {!zoomOpen && (
          <button
            type="button"
            data-testid="site-zoom-open"
            onClick={(ev) => {
              ev.stopPropagation();
              setZoomOpen(true);
            }}
            className="absolute right-2 top-2 z-20 flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-heda-ink shadow hover:bg-white"
            title="Open fullscreen — pin from a bigger view"
          >
            <Maximize2 size={11} strokeWidth={2.4} />
            Fullscreen
          </button>
        )}

        {/* Helper overlay before user starts */}
        {pins.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/15 px-4 text-center text-[12.5px] font-medium text-white">
            <div className="rounded-lg bg-heda-ink/75 px-3 py-1.5 shadow">
              {activeLetter
                ? `Tap on the surface where Material ${activeLetter} should go`
                : "Tap a material letter above, then tap the surface in this photo"}
            </div>
          </div>
        )}

        {/* Pins */}
        {pins.map((p) => {
          const color = LETTER_COLORS[p.letter] || LETTER_COLORS.A;
          // Phase 2 — does this pin have any optional hints set?
          const hasHints = Boolean(
            p.surface_type || p.treatment || p.is_open_fixture
          );
          const isOpen = openPinId === p.id;
          const isOtherOpen = openPinId && !isOpen;
          return (
            <div
              key={p.id}
              data-testid={`pin-${p.letter}-${p.id}`}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{
                left: `${p.x * 100}%`,
                top: `${p.y * 100}%`,
                // Fade non-active pins so they don't compete with the popover
                opacity: isOtherOpen ? 0.25 : 1,
                transition: "opacity 120ms ease-out",
                zIndex: isOpen ? 25 : 10,
              }}
              onClick={(ev) => {
                ev.stopPropagation();
                // Toggle this pin's popover. Tapping the same pin again closes it.
                setOpenPinId((cur) => (cur === p.id ? null : p.id));
              }}
            >
              {/* Pin body */}
              <div
                className="flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-bold text-white shadow-lg ring-2 ring-white"
                style={{ backgroundColor: color.bg }}
              >
                {p.letter}
              </div>
              {/* Subtle hint indicator — only visible if Phase 2 details set */}
              {hasHints && !isOpen && (
                <span
                  data-testid={`pin-hint-dot-${p.id}`}
                  className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-white ring-1 ring-heda-gold"
                  title="Pin has design hints"
                />
              )}
              {/* Remove (×) button — hidden whenever ANY popover is open, not just this pin's,
                  so it never overlaps the popover content visually. */}
              {!openPinId && (
                <button
                  type="button"
                  onClick={(ev) => handleRemove(p.id, ev)}
                  className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white text-heda-ink shadow ring-1 ring-heda-line hover:bg-heda-cream"
                  title="Remove pin"
                >
                  <X size={10} strokeWidth={2.6} />
                </button>
              )}
            </div>
          );
        })}

        {/* Phase 2 popover — rendered ONCE at the stage level (not inside any pin
            div) so the absolute pixel positioning is calculated against the stage,
            not the pin's CSS-transformed parent. */}
        {openPinId && (() => {
          const p = pins.find((pp) => pp.id === openPinId);
          if (!p) return null;
          const color = LETTER_COLORS[p.letter] || LETTER_COLORS.A;
          return (
            <PinDetailsPopover
              pin={p}
              letterColor={color}
              stageRef={stageRef}
              onUpdate={(next) =>
                setPins(pins.map((pp) => (pp.id === p.id ? next : pp)))
              }
              onRemove={() => {
                setPins(pins.filter((pp) => pp.id !== p.id));
                setOpenPinId(null);
              }}
              onClose={() => setOpenPinId(null)}
            />
          );
        })()}

        {/* Soft pulse on stage when a letter is active */}
        {activeLetter && (
          <div className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-heda-gold/40" />
        )}
      </div>
      </div>

      {/* Hint footer — inline mode only (fullscreen has its own header hint) */}
      {!zoomOpen && (
        <div className="mt-1.5 text-[9.5px] uppercase tracking-[0.14em] text-heda-mute/80">
          Tap a letter · tap the surface · tap a placed pin for details · Fullscreen for bigger view
        </div>
      )}
    </div>
  );
}
